import { FC, useCallback, useEffect, useState } from 'react'
import { useNotify, useRecordContext, useTranslate } from 'react-admin'
import GroupIcon from '@mui/icons-material/Group'
import {
  Box, Button, Skeleton,
  Table, TableBody, TableCell, TableHead, TableRow,
  Typography,
} from '@mui/material'
import { getJson, getUrl, httpClient, postJson } from '../providers/data'
import { LinkSeatUserOut } from '../types/apiRequests'

interface LinkUserAccessSectionProps {
  // The org-link resource path, e.g. 'organization_mcp_link' or 'organization_workflow_link'.
  resource: string
}

/**
 * Manages per-user access for an organization link. Lists the organization's
 * seat holders and lets a beheerder grant or revoke access to this specific link
 * via the link's `/seat_users` sub-resource. Reused across MCP and workflow links.
 */
export const LinkUserAccessSection: FC<LinkUserAccessSectionProps> = ({ resource }) => {
  const record = useRecordContext<{ id: string }>()
  const translate = useTranslate()
  const notify = useNotify()
  const [users, setUsers] = useState<LinkSeatUserOut[]>([])
  const [loading, setLoading] = useState(true)
  const [togglingUser, setTogglingUser] = useState<string | null>(null)

  // /seat_users is a custom sub-resource, not a standard dataprovider CRUD resource.
  const fetchUsers = useCallback(async () => {
    if (!record?.id) return
    try {
      const data = await getJson<LinkSeatUserOut[]>(
        `${getUrl()}/${resource}/${record.id}/seat_users`
      )
      setUsers(data)
    } catch {
      notify('ra.notification.http_error', { type: 'error' })
    } finally {
      setLoading(false)
    }
  }, [resource, record?.id, notify])

  useEffect(() => { void fetchUsers() }, [fetchUsers])

  const handleToggleAccess = useCallback(async (email: string, currentAccess: boolean) => {
    if (!record?.id) return
    setTogglingUser(email)
    try {
      if (currentAccess) {
        await httpClient(
          `${getUrl()}/${resource}/${record.id}/seat_users?email=${encodeURIComponent(email)}`,
          { method: 'DELETE' }
        )
      } else {
        await postJson(
          `${getUrl()}/${resource}/${record.id}/seat_users`,
          // eslint-disable-next-line camelcase
          { auth0_email: email }
        )
      }
      await fetchUsers()
    } catch {
      notify('ra.notification.http_error', { type: 'error' })
    } finally {
      setTogglingUser(null)
    }
  }, [resource, record?.id, fetchUsers, notify])

  return (
    <Box sx={{ mt: 3, width: '100%' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
        <GroupIcon sx={{ fontSize: 22, color: 'text.disabled' }} />
        <Typography sx={{ fontWeight: 700, fontSize: '1rem' }}>
          {translate('data.userAccess')}
        </Typography>
      </Box>
      {loading ? (
        <Skeleton variant="rounded" height={120} />
      ) : users.length === 0 ? (
        <Typography sx={{ color: 'text.secondary', fontSize: '0.9rem' }}>
          {translate('data.seatUsersEmpty')}
        </Typography>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: 'action.hover' }}>
              <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem', color: 'text.secondary' }}>
                {translate('data.colUser')}
              </TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem', color: 'text.secondary', width: 140 }}>
                {translate('data.colAccess')}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {users.map((u) => (
              <TableRow key={u.auth0_email} sx={{ '&:last-child td': { border: 0 } }}>
                <TableCell>
                  <Typography sx={{ fontSize: '0.875rem', fontWeight: 500 }}>
                    {u.auth0_email}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Button
                    size="small"
                    variant={u.has_access ? 'outlined' : 'contained'}
                    disabled={togglingUser === u.auth0_email}
                    onClick={() => void handleToggleAccess(u.auth0_email, u.has_access)}
                    sx={u.has_access
                      ? {
                        borderColor: 'divider',
                        color: 'text.secondary',
                        fontSize: '0.75rem',
                        textTransform: 'none',
                      }
                      : {
                        'bgcolor': 'primary.main',
                        'fontSize': '0.75rem',
                        'textTransform': 'none',
                        '&:hover': { bgcolor: 'primary.dark' },
                      }
                    }
                  >
                    {u.has_access ? translate('data.revokeAccess') : translate('data.grantAccess')}
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </Box>
  )
}
